import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Gauge, TrendingUp, TrendingDown, Minus, CheckCircle2 } from "lucide-react";

// Phase 1C — patient-facing released-only universal results. Visibility is
// enforced server-side (/physical-tests/results/mine returns only results the
// clinician explicitly released for the authenticated patient).
const Dir = ({ improved }) =>
  improved === true ? <TrendingUp size={14} className="text-[#22C55E]" />
  : improved === false ? <TrendingDown size={14} className="text-red-400" />
  : <Minus size={14} className="text-zinc-500" />;

export default function UniversalResultsPatient() {
  const [computed, setComputed] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | error

  useEffect(() => {
    let alive = true;
    api.get("/physical-tests/results/mine")
      .then((r) => { if (alive) { setComputed(r.data?.computed || []); setState("ready"); } })
      .catch(() => { if (alive) setState("error"); });
    return () => { alive = false; };
  }, []);

  if (state === "loading") {
    return <div className="fl-card fl-card-elevated p-6" data-testid="pt-mine-loading"><div className="text-zinc-500 text-sm">Loading your assessments…</div></div>;
  }
  if (state === "error") {
    return <div className="fl-card fl-card-elevated p-6" data-testid="pt-mine-error"><div className="text-zinc-500 text-sm">Couldn’t load your assessments right now. Please try again shortly.</div></div>;
  }

  return (
    <div className="fl-card fl-card-elevated p-6" data-testid="pt-mine-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="fl-heading text-xl flex items-center gap-2"><Gauge size={18} /> Released assessments</div>
        <div className="fl-label">{computed.length} test{computed.length === 1 ? "" : "s"}</div>
      </div>

      {computed.length === 0 ? (
        <div className="text-zinc-500 text-sm" data-testid="pt-mine-empty">No released assessment results yet. Your clinician will share these as you progress.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3" data-testid="pt-mine-grid">
          {computed.map((item) => {
            const sides = item.sides || {};
            return (
              <div key={item.test_definition_id} className="fl-card p-4" data-testid={`pt-mine-${item.test_definition_id.slice(0, 8)}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="fl-heading text-base">{item.name}</div>
                    <div className="text-xs text-zinc-400 mt-0.5">{item.category_label} · {item.unit}</div>
                  </div>
                  {item.symmetry && (
                    <div className="text-right">
                      <div className="text-[11px] text-zinc-500">Symmetry (LSI)</div>
                      <div className="text-lg fl-heading text-[#f47a20]">{item.symmetry.symmetry_index_pct}%</div>
                    </div>
                  )}
                </div>
                <div className="mt-3 grid gap-2">
                  {Object.entries(sides).map(([side, s]) => (
                    <div key={side} className="text-sm bg-white/5 rounded px-3 py-2 flex items-center justify-between gap-2">
                      <span className="capitalize text-zinc-300 w-20">{side}</span>
                      <span className="text-zinc-400 text-xs flex-1">
                        {s.baseline.value} <span className="text-zinc-600">({s.baseline.date})</span>
                        {" → "}<span className="text-white">{s.current.value}</span> <span className="text-zinc-600">({s.current.date})</span>
                      </span>
                      <span className="flex items-center gap-1 text-xs">
                        <Dir improved={s.improved} />
                        {s.baseline.date !== s.current.date && s.pct_change != null ? `${s.pct_change > 0 ? "+" : ""}${s.pct_change}%` : "—"}
                      </span>
                    </div>
                  ))}
                </div>
                {item.target && (
                  <div className="mt-2 text-xs flex items-center gap-2">
                    <span className="text-zinc-500">Target {item.target.comparator === "gte" ? "≥" : "≤"} {item.target.target_value}:</span>
                    {item.target.met
                      ? <span className="text-[#22C55E] flex items-center gap-1"><CheckCircle2 size={12} /> met</span>
                      : <span className="text-amber-400">{item.target.pct_to_target != null ? `${item.target.pct_to_target}% there` : "in progress"}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
